import {
  addCalendarDaysToKey,
  calendarDateKeyFromParts,
  calendarDateKeyFromWallDate,
  dateKeyInDisplayTimezone,
  formatTimezoneLabel,
  getDisplayDateFormat,
  getDisplayDateTimeFormat,
  getDisplayLocale,
  getDisplayTimeFormat,
  getDisplayTimezone,
  parseStoredUtc,
  resetDisplaySettings,
  todayInDisplayTimezone,
  toUtcIsoStringFromCompanyLocal,
  utcIsoToCompanyLocalPicker,
} from '@/utils/displayContext';
import {
  formatDateTimeWithPreset,
  formatDateWithPreset,
  formatPickerLocalDateTimeWithPreset,
  formatTimeWithPreset,
} from '@/utils/displayFormatEngine';

export function formatCurrency(
  value: number | string | null | undefined,
  currency = 'INR',
): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat(getDisplayLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

export function formatNumber(value: number | string | null | undefined, decimals = 2): string {
  const num = Number(value ?? 0);
  return num.toFixed(decimals);
}

export function formatDate(value: string | null | undefined): string {
  return formatDateWithPreset(value, getDisplayDateFormat(), getDisplayTimezone(), getDisplayLocale());
}

/** Format company-local picker datetime (`yyyy-MM-dd'T'HH:mm`) for display labels. */
export function formatPickerLocalDateTime(value: string | null | undefined): string {
  if (!value) return '';
  return formatPickerLocalDateTimeWithPreset(
    value,
    getDisplayDateTimeFormat(),
    getDisplayDateFormat(),
    getDisplayTimeFormat(),
    getDisplayLocale(),
  );
}

export function formatDateTime(value: string | null | undefined, options?: { showTimezone?: boolean }): string {
  const formatted = formatDateTimeWithPreset(
    value,
    getDisplayDateTimeFormat(),
    getDisplayDateFormat(),
    getDisplayTimeFormat(),
    getDisplayTimezone(),
    getDisplayLocale(),
  );
  if (options?.showTimezone && value) {
    const date = parseStoredUtc(value);
    if (!Number.isNaN(date.getTime())) {
      return `${formatted} ${formatTimezoneLabel(date)}`;
    }
  }
  return formatted;
}

export function formatTime(value: string | null | undefined): string {
  return formatTimeWithPreset(value, getDisplayTimeFormat(), getDisplayTimezone(), getDisplayLocale());
}

/** Format UTC ISO value for display in the active company timezone (alias for tables). */
export function formatDisplayDateTime(value: string | null | undefined): string {
  return formatDateTime(value, { showTimezone: true });
}

export function formatDisplayDate(value: string | null | undefined): string {
  return formatDate(value);
}

/** Convert a local Date picker value to UTC ISO string for API payloads. */
export function toUtcIsoString(value: Date): string {
  return value.toISOString();
}

/** Convert company-local datetime string from pickers to UTC ISO for API payloads. */
export function toUtcIsoStringFromPicker(localValue: string): string {
  return toUtcIsoStringFromCompanyLocal(localValue);
}

/** Today's calendar date in company/display timezone (yyyy-MM-dd). */
export function todayInCompanyTimezone(): string {
  return todayInDisplayTimezone();
}

export {
  utcIsoToCompanyLocalPicker,
  calendarDateKeyFromParts,
  calendarDateKeyFromWallDate,
  addCalendarDaysToKey,
  dateKeyInDisplayTimezone,
  resetDisplaySettings,
};

export {
  DISPLAY_DATE_FORMAT_OPTIONS,
  DISPLAY_DATETIME_FORMAT_OPTIONS,
  DISPLAY_TIME_FORMAT_OPTIONS,
  displayFormatExampleLabel,
} from '@/utils/displayFormatConstants';
export { previewDisplayFormats } from '@/utils/displayFormatEngine';
export type {
  DisplayDateFormatId,
  DisplayDateTimeFormatId,
  DisplayTimeFormatId,
} from '@/utils/displayFormatConstants';
